import { useEffect, useMemo, useRef, useState } from "react"
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from "@mui/material"
import { userApi } from "../../api"
import { usePermissions } from "../../permissions"

/** Organization 0 is the back office — a tenant like any other, not a blank. */
const BACK_OFFICE_ORG_ID = "0"

const EMPTY = { email: "", name: "", username: "", role_id: "", client_id: "", title: "", phone: "" }

export default function UserDialog({ open, roles, clients, onClose, onCreated }) {
  const { isBackOffice, organizationId } = usePermissions()
  const [form, setForm] = useState(EMPTY)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // null while unknown or unasked; otherwise { available, reason }.
  const [usernameCheck, setUsernameCheck] = useState(null)
  const usernameRequest = useRef(0)

  useEffect(() => {
    if (!open) return
    // A real organization id from the start. Back office is "0", not "nothing".
    setForm({ ...EMPTY, client_id: isBackOffice ? BACK_OFFICE_ORG_ID : organizationId || "" })
    setError(null)
    setUsernameCheck(null)
  }, [open, isBackOffice, organizationId])

  /*
   * Ask whether the username is free, while they type.
   *
   * It is required and unique, so without this the first news of a collision is
   * a 409 on a finished form — and the field it is about is the one they have
   * least reason to suspect. Debounced, and the ref discards a reply that a
   * later keystroke has already outrun.
   */
  useEffect(() => {
    const candidate = form.username.trim()
    if (!open || candidate.length < 3) {
      setUsernameCheck(null)
      return undefined
    }

    const ticket = (usernameRequest.current += 1)
    const timer = setTimeout(() => {
      userApi
        .checkUsername(candidate)
        .then((data) => {
          if (ticket === usernameRequest.current) setUsernameCheck(data)
        })
        .catch(() => {
          // A failed check is not a failed username. Saying nothing lets them
          // submit and find out from the server, which is where the real answer
          // is anyway.
          if (ticket === usernameRequest.current) setUsernameCheck(null)
        })
    }, 400)

    return () => clearTimeout(timer)
  }, [form.username, open])

  const set = (key) => (event) =>
    setForm((f) => ({
      ...f,
      [key]: event.target.value,
      // A role for the wrong audience would be refused on save.
      ...(key === "client_id" ? { role_id: "" } : {}),
    }))

  /* Back-office roles for organization 0, client roles for everyone else. The
     server enforces this pairing; filtering here keeps an impossible option out
     of the list rather than letting it be chosen and then rejected. */
  const eligibleRoles = useMemo(() => {
    const targetOrg = form.client_id || organizationId
    const audience = String(targetOrg) === "0" ? "back_office" : "client"
    return roles.filter((r) => r.audience === audience)
  }, [roles, form.client_id, organizationId])

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await userApi.create({
        email: form.email.trim(),
        name: form.name.trim() || null,
        username: form.username.trim(),
        role_id: form.role_id === "" ? null : Number(form.role_id),
        client_id: form.client_id === "" ? undefined : form.client_id,
        title: form.title.trim() || null,
        phone: form.phone.trim() || null,
      })
      onCreated(result)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Add user</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            {/* Username first: it is the credential, and the only one of the two
                that has to be unique. The address is where mail goes, and a team
                mailbox may be on several accounts. */}
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                label="Username"
                value={form.username}
                onChange={set("username")}
                fullWidth
                autoFocus
                required
                error={usernameCheck?.available === false}
                helperText={
                  usernameCheck?.available === false
                    ? usernameCheck.reason
                    : usernameCheck?.available
                      ? "Available"
                      : "What they sign in with. Must be unique."
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={set("email")}
                fullWidth
                required
                helperText="May be shared with other accounts"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField label="Full name" value={form.name} onChange={set("name")} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="Job title" value={form.title} onChange={set("title")} fullWidth />
            </Grid>

            {/* Organization 0 is a real tenant and is listed like any other.
                It used to be a synthetic option with an empty value, which read
                as blank once chosen — indistinguishable from choosing nothing —
                and was then dropped from the request by `|| undefined`. */}
            {isBackOffice && clients.length > 0 ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select label="Organization" value={form.client_id} onChange={set("client_id")} fullWidth>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Role"
                value={form.role_id}
                onChange={set("role_id")}
                fullWidth
                helperText="Without one they cannot reach anything"
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="">
                  <em>No role</em>
                </MenuItem>
                {eligibleRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={12}>
              {/* No password field. An administrator choosing one has to get it
                  to the person somehow — a chat message, a sticky note — and
                  supplying one suppressed the invite, so the email carrying the
                  set-password link was never the one that went out. */}
              <Alert severity="info" variant="outlined">
                They will get an email with a link to choose their own password. Nobody else ever sees it.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || usernameCheck?.available === false}>
            {busy ? "Creating…" : "Add user"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
